import { useState, useEffect } from 'react'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useAppContext } from '../context/AppContext'
import { useLanguage } from '../context/LanguageContext'
import { translations } from '../translations/translations'
import AdminForm from '../components/AdminForm'

import '../styles/Login.css'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000'

const AdminAuth = () => {
  const [isRegister, setIsRegister] = useState(false) // Toggle between login/register
  const [isCodeSent, setIsCodeSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const [code, setCode] = useState('')

  const decodeIsAdmin = (token) => {
    try {
      // JWT uses base64url — replace url-safe chars and add padding before atob
      const base64url = token.split('.')[1]
      const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(
        Math.ceil(base64url.length / 4) * 4, '='
      )
      return JSON.parse(atob(base64)).isAdmin === true
    } catch {
      return false
    }
  }

  // Registration Fields

  const navigate = useNavigate()
  const { login } = useAppContext()
  const { language, toggleLanguage } = useLanguage()
  const t = translations[language]

  /* --------------------------------------
      1️⃣ CHECK IF ALREADY LOGGED IN (Auto-login)
  -------------------------------------- */
  useEffect(() => {
    console.log('Checking auto-login...')
    const storedToken = localStorage.getItem('adminToken')
    const storedUsername = localStorage.getItem('adminUsername')
    const tokenExpiry = localStorage.getItem('adminTokenExpiry')

    if (storedToken && storedUsername && tokenExpiry) {
      const now = new Date().getTime()
      if (now < parseInt(tokenExpiry, 10)) {
        // Token is still valid, auto-login — decode isAdmin from the signed JWT
        login({ token: storedToken, username: storedUsername, isAdmin: decodeIsAdmin(storedToken) })
        navigate('/admin')
      } else {
        // Token expired, clear from storage
        localStorage.removeItem('adminToken')
        localStorage.removeItem('adminUsername')
        localStorage.removeItem('adminTokenExpiry')
      }
    }
  }, [])

  /* --------------------------------------
      HANDLE REGISTRATION
  -------------------------------------- */
  const handleRegister = async (fields) => {
    setLoading(true)
    setError('')

    try {
      setUsername(fields.username) // Save username for login
      // Create form data
      const formData = new FormData()
      formData.append('wife_firstname', fields.wife_firstname)
      formData.append('wife_lastname', fields.wife_lastname)
      formData.append('husband_firstname', fields.husband_firstname)
      formData.append('husband_lastname', fields.husband_lastname)
      formData.append('username', fields.username)
      formData.append('email', fields.email)
      formData.append('phone_number', fields.phone_number)
      formData.append('event_date', fields.event_date)
      formData.append('event_address', fields.event_address)
      formData.append('password', fields.new_password)

      // If user provided an invitation file, include it
      if (fields.invitationFile) {
        formData.append('invitation', fields.invitationFile)
      }

      await axios.post(`${BASE_URL}/admin/register`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      alert(t.registrationSuccessful)
      setIsRegister(false) // Switch to login mode
      sendCode() // Auto-send verification code
    } catch (error) {
      setError(t.registrationFailed)
      console.error('Registration error:', error)
    }
    setLoading(false)
  }

  /* --------------------------------------
      SEND VERIFICATION CODE (LOGIN)
  -------------------------------------- */
  const sendCode = async () => {
    setLoading(true)
    setError('')

    try {
      await axios.post(`${BASE_URL}/admin/send-code`, { username })
      setIsCodeSent(true)
    } catch (error) {
      setError(t.failedToSendCode)
      console.error('Failed to send code', error)
    }
    setLoading(false)
  }

  /* --------------------------------------
      VERIFY CODE AND LOGIN
  -------------------------------------- */
  const verifyCode = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await axios.post(`${BASE_URL}/admin/verify-code`, {
        username,
        code,
      })
      const { token, username: adminUsername } = response.data

      // 1) Store token & username in context — decode isAdmin from the signed JWT
      login({ token, username: adminUsername, isAdmin: decodeIsAdmin(token) })

      // 2) Store in localStorage for 1 day (86400000 ms = 24 hours)
      const expiryTime = new Date().getTime() + 86400000
      localStorage.setItem('adminToken', token)
      localStorage.setItem('adminUsername', adminUsername)
      localStorage.setItem('adminTokenExpiry', expiryTime.toString())

      // 3) Navigate to Admin Dashboard
      navigate('/admin')
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error) // Possibly "Invalid address"
      } else {
        setError(t.serverError)
      }
    }
    setLoading(false)
  }

  /* --------------------------------------
      ✅ HANDLE LOGIN (Username + Password)
  -------------------------------------- */
  const handleLogin = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!username || !password) {
      setError(t.invalidCredentials)
      setLoading(false)
      return
    }

    try {
      const response = await axios.post(`${BASE_URL}/admin/login`, {
        username,
        password,
      })

      const { token, isAdmin } = response.data

      // Store token and username in localStorage
      const expiryTime = new Date().getTime() + 86400000 // 24 hours
      localStorage.setItem('adminToken', token)
      localStorage.setItem('adminUsername', username)
      localStorage.setItem('adminTokenExpiry', expiryTime.toString())

      // Use isAdmin directly from the response — backend is the source of truth here
      login({ token, username, isAdmin: isAdmin === true })
      navigate('/admin')
    } catch (error) {
      console.error('Login failed:', error)
      setError(t.invalidCredentials)
    }
    setLoading(false)
  }

  return (
    <div>
    <div className="login-container">

      {isRegister && <h2>{t.createAdminAccount}</h2>}
      {!isRegister && <h2>{t.adminLogin}</h2>}

      {isRegister ? (
        !isCodeSent ? (
          // ----------------------
          // REGISTER FORM
          // ----------------------
          <AdminForm
            mode="register"
            onSubmit={handleRegister}
            loading={loading}
          />
        ) : (
          // ----------------------
          // LOGIN: ENTER VERIFICATION CODE
          // ----------------------
          <div className="verification-section">
            <h3>{t.enterVerificationCode}</h3>
            <p style={{fontSize: '14px', color: '#64748b', textAlign: 'center', margin: '0 0 15px 0'}}>
              {t.verificationCodeSent}
            </p>
            <input
              type="text"
              placeholder={t.enterSixDigitCode}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <button onClick={verifyCode} disabled={loading}>
              {loading && <span className="login-spinner"></span>}
              {loading ? t.verifying : t.verifyCode}
            </button>
          </div>
        )
      ) : (
        // ----------------------
        // LOGIN: ENTER USERNAME AND PASSWORD
        // ----------------------
        <div className='login-form'>
          <label>{t.username}</label>
          <input
            type="text"
            placeholder={t.enterUsername}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <label>{t.password}</label>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={t.enterPassword}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ paddingRight: '44px', width: '100%', boxSizing: 'border-box' }}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
            </button>
          </div>
          {error && (
            <div className="login-error-message" style={{ color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 16px', fontSize: '14px', fontWeight: 500, marginTop: '12px', display: 'block', width: '100%', boxSizing: 'border-box' }}>
              {error}
            </div>
          )}
          <button
            onClick={handleLogin}
            disabled={loading}
          >
            {loading && <span className="login-spinner"></span>}
            {loading ? t.signingIn : t.signIn}
          </button>
          <div
            className="login-forgot-password"
            onClick={() => navigate('/code')}
          >
            {t.forgotPassword}
          </div>
        </div>
      )}

    </div>
    </div>
  )
}

export default AdminAuth
